import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Divider,
  message,
  Card,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
const { TextArea } = Input;
const { Option } = Select;

const categories = ["scientific", "societal", "economic", "environmental"];
const indicatorNames = [
  "CO2 Emissions Reduced",
  "Energy Saved (kWh)",
  "Publications Produced",
  "Patents Filed",
  "Jobs Created",
  "Training Sessions Held",
  "Stakeholders Engaged",
  "New Policies Influenced",
  "Technology Readiness Level Improved",
  "Environmental Footprint Decreased",
  "Market Uptake Initiated",
  "Citizen Awareness Raised",
  "Educational Materials Developed",
  "Software Tools Developed",
  "Mobility Improved",
  "Water Usage Reduced",
  "Renewable Energy Deployed",
  "Waste Recycled",
  "Innovation Score Increased",
  "Greenhouse Gas Intensity Reduced",
  // You can add more indicators as needed
];
const units = ["#", "%", "kg", "tonnes", "EUR", "hours"];

const KIPForm = ({ projectId, partnerId, token }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selectedIndicators, setSelectedIndicators] = useState("");

  const handleChange = (value) => {
    setSelectedIndicators(value);
  };
  const onFinish = async (values) => {
    const formattedSteps = values.steps.map((step) => ({
      title: step.title || "Step Title", // Add title from form or set a default
      description: step.step_description, // Rename for backend
      means_of_verification: step.means_of_verification,
      date: step.date.format("YYYY-MM-DD"),
      status: step.status,
      indicators: step.indicators.map((ind) => ({
        name: ind.name[0], // If it's an array, pick the first one
        value: parseFloat(ind.value),
        unit: ind.unit,
      })),
    }));

    try {
      console.log("formattedSteps", formattedSteps);
      await axios.post(
        "https://eutracker.rdiup.com/v1/api/kips/create",
        {
          title: values.title,
          description: values.description,
          category: values.category,
          status: values.status, // <-- Add this line

          project: localStorage.getItem("project_id"),
          partner: localStorage.getItem("partner"),
          steps: formattedSteps,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      message.success("KIP created successfully");
      navigate("/impacts");
      form.resetFields();
    } catch (error) {
      console.error("Error creating KIP:", error);
      message.error("Failed to create KIP");
    }
  };

  return (
    <Card
      title="Key Impact Pathway Form"
      bordered={false}
      style={{ margin: 24 }}
    >
      <Form
        form={form}
        name="kip_form"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Please input the title!" }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: "Please select a category!" }]}
            >
              <Select placeholder="Select category">
                {categories.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: "Please input the description!" }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Divider orientation="left">Pathway Steps</Divider>

        <Form.List name="steps" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card size="small" key={key} style={{ marginBottom: 16 }}>
                  <Row gutter={16} align="middle">
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "title"]}
                        rules={[
                          {
                            required: true,
                            message: "Missing step title",
                          },
                        ]}
                      >
                        <Input placeholder="title" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "date"]}
                        rules={[{ required: true, message: "Missing date" }]}
                      >
                        <DatePicker
                          placeholder="Date"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "step_description"]}
                        rules={[
                          {
                            required: true,
                            message: "Missing step description",
                          },
                        ]}
                      >
                        <Input placeholder="Step description" />
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "means_of_verification"]}
                        rules={[
                          {
                            required: true,
                            message: "Missing means of verification",
                          },
                        ]}
                      >
                        <Input placeholder="Means of verification" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        name="status"
                        rules={[
                          {
                            required: true,
                            message: "Please select a status!",
                          },
                        ]}
                      >
                        <Select placeholder="Select status">
                          <Option value="planned">Planned</Option>
                          <Option value="in_progress">In Progress</Option>
                          <Option value="completed">Completed</Option>
                          <Option value="deplayed">Delayed</Option>
                          <Option value="cancelled">Cancelled</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>

                  <Divider orientation="left">Indicators</Divider>
                  <Form.List name={[name, "indicators"]}>
                    {(
                      indicatorFields,
                      { add: addIndicator, remove: removeIndicator }
                    ) => (
                      <>
                        {indicatorFields.map(
                          ({ key: indKey, name: indName, ...indRest }) => (
                            <Row
                              gutter={16}
                              key={indKey}
                              style={{ marginBottom: 8 }}
                            >
                              <Col span={4}>
                                <Form.Item
                                  {...indRest}
                                  name={[indName, "name"]}
                                  rules={[{ required: true }]}
                                >
                                  <Select
                                    mode="tags"
                                    style={{ width: "100%" }}
                                    placeholder="Select or add indicator name(s)"
                                    value={selectedIndicators}
                                    onChange={handleChange}
                                    showSearch
                                    allowClear
                                  >
                                    {indicatorNames.map((indicator) => (
                                      <Option key={indicator} value={indicator}>
                                        {indicator}
                                      </Option>
                                    ))}
                                  </Select>
                                  {/*  <Select placeholder="Indicator Name">
                                    {indicatorNames.map((i) => (
                                      <Option key={i} value={i}>
                                        {i}
                                      </Option>
                                    ))}
                                  </Select> */}
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item
                                  {...indRest}
                                  name={[indName, "value"]}
                                  rules={[{ required: true }]}
                                >
                                  <Input type="number" placeholder="Value" />
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item
                                  {...indRest}
                                  name={[indName, "unit"]}
                                  rules={[{ required: true }]}
                                >
                                  <Select placeholder="Unit">
                                    {units.map((u) => (
                                      <Option key={u} value={u}>
                                        {u}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col span={2}>
                                <MinusCircleOutlined
                                  onClick={() => removeIndicator(indName)}
                                />
                              </Col>
                            </Row>
                          )
                        )}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => addIndicator()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Indicator
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Card>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Step
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default KIPForm;
